import React, { useState, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaUser,
  FaUserTie,
  FaLock,
  FaUserCog,
  FaIdBadge,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { registerUser, updateUserByID } from "../api/api";

// ✅ Dropdown Options
const USER_TYPE_OPTIONS = [
  { label: "Subordinate", value: "subordinate" },
  { label: "Operator", value: "operator" },
];

// ✅ Role Options
const ROLE_OPTIONS = [
  { label: "Merchant Onboarding", value: "merchant_onboarding" },
  { label: "Terminal Creation", value: "terminal_creation" },
  { label: "View Only", value: "view-only" },
];

// ✅ Initial Form Values
const INITIAL_FORM = {
  username: "",
  name: "",
  user_type: "",
  roles: [],
  password: "",
};

export default function CreateUser() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const mode = location.state?.mode || "create";
  const initialData = location.state?.user || {};

  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";
  const isCreateMode = mode === "create";

  // ✅ Prepare initial form values
  const formInitialValues = useMemo(() => {
    if (isEditMode || isViewMode) {
      return {
        ...INITIAL_FORM,
        username: initialData.username || "",
        name: initialData.name || "",
        user_type: initialData.user_type || "",
        roles: initialData.roles || [],
        password: "",
      };
    }
    return INITIAL_FORM;
  }, [isEditMode, isViewMode, initialData]);

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .matches(/^[A-Za-z0-9]+$/, "Only letters and digits allowed, no spaces")
      .min(3, "At least 3 characters long")
      .required("User Name is required"),

    name: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "Only letters and spaces allowed")
      .required("Name is required"),

    user_type: Yup.string()
      .oneOf(["subordinate", "operator"], "Invalid User Type")
      .required("User Type is required"),

    roles: Yup.array()
      .of(
        Yup.string().oneOf(
          ["merchant_onboarding", "terminal_creation", "view-only"],
          "Invalid Role"
        )
      )
      .min(1, "At least one Role is required")
      .required("Role is required"),

    // ✅ Password required only in create mode
    password: isCreateMode
      ? Yup.string()
          .min(6, "Password must be at least 6 characters long")
          .required("Password is required")
      : Yup.string().notRequired(),
  });

  // ✅ Handle form submission
  const handleSubmit = async (values, { resetForm }) => {
    if (isViewMode) return;

    setLoading(true);
    try {
      const payload = {
        username: values.username,
        name: values.name,
        user_type: values.user_type,
        roles: values.roles, // ✅ already array of strings
        password: values.password,
      };
      const updated_payload = {
        name: values.name,
        user_type: values.user_type,
        roles: values.roles,
      };
      console.log(
        "hellofsd",
        isCreateMode ? JSON.stringify(payload, null, 2) : updated_payload
      );
      const result = isCreateMode
        ? await registerUser(payload)
        : await updateUserByID(initialData.id, updated_payload);
      console.log("result", result);
      if (result) {
        alert(result.message);
        if (result.isSuccess) {
          navigate("/dashboard/userslist");
        }
      }
    } catch (err) {
      console.log("Error saving user:", err);
      alert("Something went wrong while saving user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-terminal-page">
      <div className="create-terminal-container">
        <Formik
          initialValues={formInitialValues}
          validationSchema={isViewMode ? null : validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form className="create-terminal-form">
              {/* Username */}
              <div className="input-with-icon">
                <FaUser className="input-icon" />
                <Field
                  name="username"
                  as={InputText}
                  placeholder="User Name"
                  className="onboard-input"
                  disabled={isViewMode || isEditMode}
                  onChange={(e) => {
                    const onlyLettersAndDigits = e.target.value.replace(
                      /[^A-Za-z0-9]/g,
                      ""
                    );
                    setFieldValue("username", onlyLettersAndDigits);
                  }}
                />
              </div>
              <ErrorMessage
                name="username"
                component="div"
                className="error-text"
              />

              {/* Name */}
              <div className="input-with-icon">
                <FaUserTie className="input-icon" />
                <Field
                  name="name"
                  as={InputText}
                  placeholder="Name"
                  className="onboard-input"
                  disabled={isViewMode}
                  onChange={(e) => {
                    const onlyLetters = e.target.value.replace(
                      /[^A-Za-z\s]/g,
                      ""
                    );
                    setFieldValue("name", onlyLetters);
                  }}
                />
              </div>
              <ErrorMessage
                name="name"
                component="div"
                className="error-text"
              />

              {/* User Type Dropdown */}
              <div className="input-with-icon">
                <FaUserCog className="input-icon" />
                <Dropdown
                  name="user_type"
                  value={values.user_type}
                  options={USER_TYPE_OPTIONS}
                  onChange={(e) => setFieldValue("user_type", e.value)}
                  placeholder="Select User Type"
                  className="onboard-input w-full"
                  disabled={isViewMode}
                />
              </div>
              <ErrorMessage
                name="user_type"
                component="div"
                className="error-text"
              />

              {/* Password — only for create mode */}
              {isCreateMode && (
                <>
                  {" "}
                  <div className="input-with-icon password-input-wrapper">
                    <FaLock className="input-icon" />

                    <div className="password-field-container">
                      <Field
                        name="password"
                        type={showPassword ? "text" : "password"}
                        as={InputText}
                        placeholder="Password"
                        className="onboard-input password-input"
                      />
                      <span
                        className="password-toggle-icon"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                  </div>
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="error-text"
                  />
                </>
              )}

              {/* ✅ Roles as Checkboxes */}
              <div className="roles-section">
                <div className="roles-header">
                  <FaIdBadge className="input-icon" />
                  <label className="roles-label">Assign Roles</label>
                </div>
                <div className="roles-list">
                  {ROLE_OPTIONS.map((role) => (
                    <label key={role.value} className="role-checkbox">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        name="roles"
                        value={role.value}
                        checked={values.roles.includes(role.value)}
                        disabled={isViewMode}
                        onChange={(e) => {
                          let updatedRoles = [...values.roles];

                          if (e.target.checked) {
                            // If "view-only" is selected → remove all others
                            if (role.value === "view-only") {
                              updatedRoles = ["view-only"];
                            } else {
                              // If any other role is selected → remove "view-only"
                              updatedRoles = updatedRoles.filter(
                                (r) => r !== "view-only"
                              );
                              updatedRoles.push(role.value);
                            }
                          } else {
                            // Unchecked → remove that role
                            updatedRoles = updatedRoles.filter(
                              (r) => r !== role.value
                            );
                          }

                          setFieldValue("roles", updatedRoles);
                        }}
                      />
                      <span>{role.label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage
                  name="roles"
                  component="div"
                  className="error-text"
                />
              </div>

              {/* Submit Button */}
              {!isViewMode && (
                <Button
                  type="submit"
                  label={
                    loading
                      ? "Saving..."
                      : isEditMode
                      ? "Update User"
                      : "Create User"
                  }
                  disabled={loading}
                  className="create-terminal__submit"
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
